import * as Contract from '@tableau/api-internal-contract-js';
import {
  CommandMessage,
  CreateVersionConverter,
  ErrorCodes,
  InternalContractVersionConverter,
  Messenger,
} from '@tableau/api-internal-contract-js';
import * as PresLayerContract from '@tableau/preslayer-api-contract';
import { Disposable } from '../Disposable';
import { ApiUtils } from '../common-bootstrap/ApiUtils';
import { telemetryFunctionComplete } from '../telemetry/EmbeddingTelemetryDispatcher';
import { FunctionResults, telemetryFunctionStart } from '../telemetry/TelemetryDispatcher';
import { EmbeddingApiEventHandler } from './EmbeddingApiEventHandler';

export class MessageHandler implements Disposable {
  private _versionConverter: InternalContractVersionConverter;
  private _apiEventHandler: EmbeddingApiEventHandler;

  public constructor(private _messenger: Messenger, presLayer: PresLayerContract.PresentationLayer, versionNumber: Contract.VersionNumber) {
    if (!this._messenger) {
      throw 'Missing messenger';
    }
    this._versionConverter = CreateVersionConverter(versionNumber, Contract.INTERNAL_CONTRACT_VERSION);
    this._apiEventHandler = new EmbeddingApiEventHandler(presLayer);
    this._apiEventHandler.setEventHandler(this.onNotification.bind(this));
    _messenger.setCommandMessageHandler(this.onCommandMessage.bind(this));
    _messenger.startListening();
  }

  private onCommandMessage(message: CommandMessage, source: Window): void {
    const upgradedParams = this._versionConverter.upgradeExecuteCall(message.verbId, message.parameters);
    const msg: CommandMessage = {
      verbId: upgradedParams.verb,
      parameters: upgradedParams.parameters,
      msgGuid: message.msgGuid,
      msgType: message.msgType,
    };
    const runtimeData = telemetryFunctionStart(); // this timer is only used in case of exceptions during execute

    this._apiEventHandler
      .execute(msg.verbId, msg.parameters)
      .then(
        (result) => {
          const downgradedResponse = this._versionConverter.downgradeExecuteReturn(result);
          this._messenger.prepareCommandResponseMessage(msg.msgGuid, downgradedResponse.result, undefined).send();
        },
        (error) => {
          const errorMessage = ApiUtils.getErrorMessageFromException(error);
          telemetryFunctionComplete(msg.verbId, runtimeData, msg.parameters, FunctionResults.Error(errorMessage));
          this._messenger
            .prepareCommandResponseMessage(msg.msgGuid, undefined, { message: errorMessage, errorCode: ErrorCodes.INTERNAL_ERROR })
            .send();
        },
      )
      .catch((err) => {
        const errorMessage = ApiUtils.getErrorMessageFromException(err);
        telemetryFunctionComplete(msg.verbId, runtimeData, msg.parameters, FunctionResults.Error(errorMessage));
        this._messenger.prepareCommandResponseMessage(msg.msgGuid, undefined, err).send();
      });
  }

  private onNotification(notification: Contract.NotificationId, model: Contract.Model): void {
    // Downgrade the notification to what the embedding external contract is expecting
    const downgradedNotification = this._versionConverter.downgradeNotification({ notificationId: notification, data: model });

    // When we get a notification from the pres layer, forward it on to the extension
    this._messenger.prepareNotificationMessage(downgradedNotification.notificationId, downgradedNotification.data).send();
  }

  public dispose(): void {
    this._messenger.stopListening();
    this._apiEventHandler.dispose();
  }
}
